import { Controller } from "@hotwired/stimulus"

// FIND A COMPONENT WITHOUT KNOWING WHICH TAB IT IS IN.
//
// The page is six tabs holding fourteen sections, and its one job is to be
// checked before somebody writes a class. Tabs fixed the scrolling; they did
// not fix the harder half, which is that answering "is there already a
// component for X" still requires knowing where X lives. Guessing wrong
// costs a tab switch and a scroll, and the failure this page exists to
// prevent — a second .fp-chip beside the existing .chip — is exactly what
// happens when somebody gives up on that.
//
// So the filter searches ACROSS the tabs, not within the open one. Typing
// "dropdown" while Foundations is open has to find Fields, or it has taught
// you that the page does not have one.
//
// It matches the section titles, the search terms declared beside them, and
// every class name the page draws — because ".chip" is what a person
// actually arrives with, more often than a section title is.
export default class extends Controller {
  static targets = ["input", "result", "results", "empty", "count"]

  connect() {
    // The whole list, hidden, until somebody types. A jump list that is
    // always open pushes the page down and is a second thing to scroll past.
    this.close()

    // A FLOATING PANEL HAS TO BE DISMISSABLE BY CLICKING AWAY.
    //
    // Blur alone is not enough, for the reason select_search records beside
    // its own copy of this: clicking a heading or empty page moves focus
    // nowhere, so no blur fires and the panel stays open over whatever you
    // clicked. mousedown rather than click, and capture, so it beats a
    // result's own mousedown for a click landing outside — while a click
    // INSIDE is let through by the containment check, which is what lets
    // jump() run at all.
    this.onOutsideClick = (event) => {
      if (this.resultsTarget.hidden) return
      if (this.element.contains(event.target)) return

      this.close()
    }
    document.addEventListener("mousedown", this.onOutsideClick, true)
  }

  disconnect() {
    document.removeEventListener("mousedown", this.onOutsideClick, true)
  }

  // `/` FOCUSES THE FILTER, which is the shortcut every reference page has
  // and the reason this is worth a keyboard handler at all — the point is to
  // get an answer in seconds, and reaching for the mouse is most of them.
  //
  // Ignored while typing anywhere else, so it does not steal a slash from a
  // field on the page. There are several: this page is full of them.
  focusFilter(event) {
    if (event.key !== "/") return

    const tag = document.activeElement?.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return

    event.preventDefault()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()

    if (query === "") {
      this.close()
      return
    }

    let shown = 0
    this.resultTargets.forEach((result) => {
      // Title, declared terms and class names, lowercased server-side into
      // one attribute — so the match is a substring test rather than this
      // controller knowing anything about the page's structure.
      const hit = result.dataset.terms.includes(query)
      result.hidden = !hit
      if (hit) shown += 1
    })

    this.resultsTarget.hidden = false
    this.emptyTarget.hidden = shown > 0
    this.countTarget.textContent = shown === 1 ? "1 section" : `${shown} sections`
  }

  close() {
    this.resultsTarget.hidden = true
    this.emptyTarget.hidden = true
  }

  // ESCAPE CLEARS RATHER THAN BLURS. A filter you have to empty by hand is
  // one people leave filtered, then wonder why the page is missing things.
  clear(event) {
    if (event.key !== "Escape") return
    this.inputTarget.value = ""
    this.close()
  }

  // JUMPING ACROSS TABS IS TWO ACTS: switch to the tab holding the section,
  // then scroll to it. A plain #anchor does neither — the target is inside a
  // `hidden` panel, so the browser scrolls to a element it cannot show and
  // the page appears not to have moved.
  jump(event) {
    event.preventDefault()
    const { tab, section } = event.currentTarget.dataset

    // Click the real tab button rather than reaching into the tabs
    // controller, so its own bookkeeping — the remembered choice, the strip
    // scroll — happens exactly as it does for a click, and this controller
    // stays ignorant of how tabs work.
    document.querySelector(`[data-tabs-target="tab"][data-tab="${tab}"]`)?.click()

    const heading = document.getElementById(section)
    if (!heading) return

    // After the panel is visible: scrolling to something still hidden lands
    // at the wrong offset. The tab switch is synchronous, so one frame is
    // enough, and this avoids a timeout that would be a guess.
    requestAnimationFrame(() => {
      heading.scrollIntoView({ behavior: "smooth", block: "start" })
    })

    this.inputTarget.value = ""
    this.close()
  }
}
